/**
 * Session swapping for the platform test environment.
 *
 * A platform admin "operates as" a sandbox user by asking the server for a
 * custom token for that user and signing in with it; the resulting session
 * carries a `testSession` claim. From inside that session the admin can hop
 * directly to another test account, or ask for a token that returns the
 * browser to the platform-admin account. Every call is validated
 * server-side (functions/lib/testEnvironment.js) — there is never a
 * password or a login form involved.
 */
import { getFunctions, httpsCallable } from "firebase/functions";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "../firebase";
import { formatError } from "./formatError";
import { ACCOUNT_SWAP_BEGIN_EVENT, ACCOUNT_SWAP_FAILED_EVENT } from "../hooks/useAuth";
import type { TestEnvironmentSummary } from "../hooks/usePlatformAdmin";

interface TokenResponse {
  token: string;
  user?: { id: string; name: string; role: string; agencyId: string | null };
}

const ACCOUNTS_CACHE_KEY = "ood:testEnvironmentAccounts";

async function swapSession(functionName: string, payload?: Record<string, unknown>): Promise<TokenResponse> {
  let data: TokenResponse;
  try {
    const fn = httpsCallable<Record<string, unknown> | undefined, TokenResponse>(getFunctions(), functionName);
    const result = await fn(payload);
    data = result.data;
  } catch (err) {
    throw new Error(formatError(err));
  }
  if (!data?.token) {
    throw new Error("The server did not return a sign-in token.");
  }
  // Tear the current account down first (see useAuth) so its listeners are
  // gone before the new token is live, then swap.
  window.dispatchEvent(new Event(ACCOUNT_SWAP_BEGIN_EVENT));
  try {
    await signInWithCustomToken(auth, data.token);
  } catch (err) {
    window.dispatchEvent(new Event(ACCOUNT_SWAP_FAILED_EVENT));
    throw new Error(formatError(err));
  }
  return data;
}

/** Operate as the given sandbox user (from the platform panel or from another test account). */
export function enterTestSession(userId: string): Promise<TokenResponse> {
  return swapSession("enterTestEnvironment", { userId });
}

/** Return from a sandbox session to the platform-admin account that opened it. */
export function exitTestSession(): Promise<TokenResponse> {
  return swapSession("exitTestEnvironment");
}

/**
 * Account list for the in-app switcher. Cached in sessionStorage so the
 * dropdown is populated instantly after each hop; refreshed in the
 * background on every mount.
 */
export function readCachedTestAccounts(): TestEnvironmentSummary | null {
  try {
    const raw = sessionStorage.getItem(ACCOUNTS_CACHE_KEY);
    return raw ? (JSON.parse(raw) as TestEnvironmentSummary) : null;
  } catch {
    return null;
  }
}

export async function fetchTestAccounts(): Promise<TestEnvironmentSummary> {
  let data: TestEnvironmentSummary;
  try {
    const fn = httpsCallable<{ accountsOnly: boolean }, TestEnvironmentSummary>(getFunctions(), "getTestEnvironment");
    const result = await fn({ accountsOnly: true });
    data = result.data;
  } catch (err) {
    throw new Error(formatError(err));
  }
  try {
    sessionStorage.setItem(ACCOUNTS_CACHE_KEY, JSON.stringify(data));
  } catch {
    // Storage unavailable (private mode, quota) — the live fetch still worked.
  }
  return data;
}

export function clearCachedTestAccounts() {
  try {
    sessionStorage.removeItem(ACCOUNTS_CACHE_KEY);
  } catch {
    // ignore
  }
}
